import PropTypes from 'prop-types'
import { DEFAULTS, STEPS } from './constants'
import ColumnCheck from './ColumnCheck'
import FileSelect from './FileSelect'
import Heading from './Heading'
import Review from './Review'
import Upload from './Upload'
import Loading from 'Shared/components/Loading'
import MatchStrategy from './MatchStrategy'
import { getCsvSample, MATCHING_COLUMNS } from './helpers'

function getKind(type) {
  if (type === 'upload-people') return 'members'
  if (type === 'upload-list') return 'list'
  if (type === 'upload-custom') return 'custom_fields'
}

function computeDefaultMatchFields(csvHeaders) {
  const available = MATCHING_COLUMNS
    .filter(({ label }) => csvHeaders.some(h => h.trim().toLowerCase() === label.toLowerCase()))
    .map(({ field }) => field)
  if (available.includes('external_id')) {
    return ['external_id', ...available.filter(f => f !== 'external_id')]
  }
  return available
}

const CommaChameleon = ({ type, schema, upload, matchMode = false, labels }) => {
  const config = { ...DEFAULTS.labels, ...labels }
  const allowNonExternalIdMatching = !!(window.ENV.SETTINGS['allow_target_non_external_id_matching'])

  const steps = useMemo(() => {
    if (allowNonExternalIdMatching && type === 'upload-people') return STEPS
    return STEPS.filter(s => !s.conditional)
  }, [allowNonExternalIdMatching, type])

  const [display, setDisplay] = useState(steps[0].id)
  const [mergedSchema, setMergedSchema] = useState(schema)
  const [csv, setCsv] = useState()
  const [listName, setListName] = useState('')
  const [uploadSupporters, setUploadSupporters] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [confirmedColumns, setConfirmedColumns] = useState([])
  const [uploadType, setUploadType] = useState('')
  const [csvHeaders, setCsvHeaders] = useState([])
  const [matchFields, setMatchFields] = useState([])
  const kind = getKind(type)

  const resetState = () => {
    setDisplay(steps[0].id)
    setCsv()
  }

  useEffect(() => {
    resetState()
  }, [])

  useEffect(() => {
    if (matchMode) {
      setLoaded(true)
    }

    const getCustomFields = axios.get('/api/custom_fields?only_modifiable=true')
    const getSubscriptions = axios.get('/api/subscriptions/schema')

    Promise.all([getCustomFields, getSubscriptions]).then((responses) => {
      const customFields = responses[0].data.filter((r) => r.name && r.name.trim() !== '').map((r) => ({
        name: r.name,
        required: false,
        type: r.type,
        regex: /.*/,
      }))
      const subscriptions = _.flatten(_.map(responses[1].data, (rows, kind) => {
        return rows.map((r) => ({
          name: `Subscription ${_.startCase(kind)} ${r.topic}`,
          required: false,
          type: r.type,
          regex: /^$|y|n/i,
        }))
      }))
      const fullSchema = schema.concat(customFields).concat(subscriptions)
      const adjustedSchema = (allowNonExternalIdMatching && type === 'upload-people')
        ? fullSchema.map((f) => f.name === 'Membership Number' ? { ...f, required: false } : f)
        : fullSchema
      setMergedSchema(adjustedSchema)
      setLoaded(true)
    })
  }, [])

  useEffect(() => {
    if (!csv || !(allowNonExternalIdMatching && type === 'upload-people')) return
    getCsvSample(csv, 1).then((sample) => {
      const headers = sample.data[0] || []
      setCsvHeaders(headers)
      setMatchFields(computeDefaultMatchFields(headers))
    })
  }, [csv])

  const onCsvSelected = ({ type: csvType, ref }) => {
    setCsv({ type: csvType, ref })
    setDisplay('review')
  }

  const onCancel = (goto, keepCsv = false) => {
    if (goto === 'upload') {
      goto = allowNonExternalIdMatching && type === 'upload-people' ? 'match' : 'review'
    }
    if (!keepCsv) setCsv()
    setDisplay(goto)
  }

  const onCsvValidated = () => {
    if (allowNonExternalIdMatching && type === 'upload-people') {
      setDisplay('match')
    } else {
      setDisplay('upload')
    }
  }

  const onCsvUpdated = (newCsv) => {
    setCsv(newCsv)
  }

  if (!loaded) {
    return <Loading />
  }

  return (
    <>
      <div className="cc-content">
        <Heading step={display} steps={steps} />
        <ColumnCheck
          step="check"
          type={type}
          show={display === 'check'}
          labels={config}
          columns={mergedSchema}
          confirmedColumns={confirmedColumns}
          setConfirmedColumns={setConfirmedColumns}
          onComplete={() => setDisplay('select')}
          onUploadTypeChange={setUploadType}
          allowNonExternalIdMatching={allowNonExternalIdMatching}
          steps={steps}
        />
        <FileSelect
          show={display === 'select'}
          step="select"
          onCancel={() => onCancel('check')}
          onSubmit={onCsvSelected}
          type={type}
          confirmedColumns={confirmedColumns}
          steps={steps}
        />
        <Review
          show={display === 'review'}
          type={type}
          step="review"
          csv={csv}
          schema={mergedSchema}
          onCancel={() => onCancel('select')}
          onValidated={onCsvValidated}
          onCsvUpdated={onCsvUpdated}
          listName={listName}
          setListName={setListName}
          needsList={matchMode}
          uploadSupporters={uploadSupporters}
          setUploadSupporters={setUploadSupporters}
          confirmedColumns={confirmedColumns}
          steps={steps}
        />
        {allowNonExternalIdMatching && type === 'upload-people' && (
          <MatchStrategy
            show={display === 'match'}
            step="match"
            steps={steps}
            csv={csv}
            csvHeaders={csvHeaders}
            matchFields={matchFields}
            setMatchFields={setMatchFields}
            uploadType={uploadType}
            onCancel={() => onCancel('review', true)}
            onComplete={() => setDisplay('upload')}
          />
        )}
        <Upload
          show={display === 'upload'}
          upload={upload}
          csv={csv}
          onCancel={() => onCancel('upload')}
          listName={listName}
          kind={kind}
          uploadSupporters={uploadSupporters}
          confirmedColumns={confirmedColumns}
          matchFields={matchFields}
        />
      </div>
    </>
  )
}

CommaChameleon.propTypes = {
  type: PropTypes.string.isRequired,
  schema: PropTypes.arrayOf(PropTypes.object).isRequired,
  upload: PropTypes.object.isRequired,
}

export default CommaChameleon
